import { AntdThemeProvider } from '@/contexts/AntdTheme';
import { ReactQueryProvider } from '@/contexts/ReactQuery';
import { TanStackRouterProvider } from '@/contexts/Router';
import { AuthProvider } from '@/contexts/AuthContext';
import { ModuleProvider } from '@/contexts/ModuleContext';
import { AdminClientsProvider } from '@/contexts/AdminClientsContext';
import { ReminderProvider } from '@/contexts/ReminderContext';

import { captureException } from '@/lib/sentry';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { App as AntdApp, Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { queryClient } from '@/contexts/ReactQuery';
import '@/assets/scss/page-transitions.scss';

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();
  return (
    <Result
      status="500"
      title={t('errors.somethingWentWrong')}
      subTitle={error instanceof Error ? error.message : String(error)}
      extra={
        <Button onClick={resetErrorBoundary} type="primary">
          {t('actions.reset')}
        </Button>
      }
    />
  );
}

const App: React.FC = () => {
  return (
    <AntdApp>
      <AntdThemeProvider>
        <ReactQueryProvider>
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, info) => {
              captureException(error, { componentStack: info.componentStack });
            }}
            onReset={() => {
              queryClient.clear();
              window.location.href = '/';
            }}>
            <AuthProvider>
              <AdminClientsProvider>
                <ModuleProvider>
                  <ReminderProvider>
                    <TanStackRouterProvider />
                  </ReminderProvider>
                </ModuleProvider>
              </AdminClientsProvider>
            </AuthProvider>
          </ErrorBoundary>
        </ReactQueryProvider>
      </AntdThemeProvider>
    </AntdApp>
  );
};

export default App;
